import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GlassHeroContainer } from '../../components/glassmorphism/GlassmorphismStyles';
import GetStarted from '../../components/GetStarted/GetStarted';

const RefundPage: React.FC = () => {
  return (
    <GlassHeroContainer>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: '96px', sm: '136px' },
          pb: { xs: '24px', sm: '48px' },
          px: { xs: '22px', sm: '24px' },
        }}
      >
        <PrivacyContainer>
          {/* Header Section */}
          <HeaderSection>
            <HeadingWrapper>
              <MainHeading>Refund and Cancellation Policy</MainHeading>
              <SubHeaderRow>
                <LastUpdated>Last updated: 23 Feb 2026</LastUpdated>
              </SubHeaderRow>
            </HeadingWrapper>
          </HeaderSection>

          <ContentSection>
            <SectionContentWrapper>
              <BodyText>
                At Womapp, we are committed to providing a fair, secure, and
                transparent marketplace for both Customers and Service
                Providers. Please note that Womapp acts solely as an
                intermediary platform and payment collection agent; the contract
                for the provision of services is made directly between the
                Customer and the Service Provider. This policy outlines your
                rights regarding cancellations, refunds, and dispute resolution
                in accordance with UK consumer law, including the Consumer
                Contracts Regulations 2013 and the Consumer Rights Act 2015. 
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>
                1. Your Statutory Right to Cancel (Cooling-off Period){' '}
              </SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Under UK law, when you book a service online, you generally have
                a statutory right to cancel the contract within a 14-day
                "cooling-off" period and receive a full refund. However, please
                note:
                <br />
                <ul>
                  <li>
                    If you request a Service Provider to perform the service
                    within this 14-day window, you expressly consent to the
                    service starting before the end of the cooling-off period.
                  </li>
                  <li>
                    If you cancel after the Service Provider has already started
                    the work, you will be required to pay for the portion of the
                    service that has been provided up to the point of
                    cancellation.
                  </li>
                  <li>
                    If the service is fully completed within the 14 days, you
                    lose your statutory right to cancel.{' '}
                  </li>
                </ul>
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>
                2. Standard Cancellation Policy (Before the Task){' '}
              </SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We understand that plans change. Our standard cancellation rules
                are designed to be flexible for Customers while respecting the
                time of our Service Providers. 
                <br />
                <ul>
                  <li>
                    Cancellation by Customer (More than 24 hours notice): You
                    can cancel a booked task up to 24 hours before the scheduled
                    start time without any penalty. A full refund (including any
                    platform fees) will be issued.
                  </li>
                  <li>
                    Late Cancellation (Less than 24 hours notice): If you cancel
                    within 24 hours of the agreed start time, or if you are
                    unavailable at the location when the Service Provider
                    arrives (a "no-show"), a separate cancellation fee of 10%
                    will be applied to compensate the Provider for their lost
                    time and travel. For service categories requiring the
                    physical presence of a Provider (excluding the "Business &
                    Freelance" category), this fee is subject to a minimum
                    charge of £15.
                  </li>
                  <li>
                    Cancellation by Service Provider: If the Service Provider
                    cancels the task, fails to show up, or reschedules to a time
                    that no longer works for you, you will receive a full,
                    automatic refund.{' '}
                  </li>
                </ul>
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>3. Platform Fees </SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Womapp charges Service Providers a 10% platform fee calculated
                on the total service price paid by the Customer. The fee is
                automatically deducted at the time of payment before payout to
                the Service Provider.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>
          {/* Section 4: How we use your data */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>
                4. Refunds for Unsatisfactory Service (After the Task){' '}
              </SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Under the Consumer Rights Act 2015, any service you pay for must
                be carried out with reasonable care and skill. Because payments
                are held until the service is marked complete, you are
                protected. Do not release the payment if you are not satisfied
                with the work.
                <br />
                If the work does not meet the agreed-upon standards: 
                <ol>
                  <li>
                    Right to Repair: You have the right to ask the Service
                    Provider to fix the issue or complete the service properly
                    at no extra cost.
                  </li>
                  <li>
                    Right to Refund: If the issue cannot be fixed, or if the
                    Service Provider refuses or fails to fix it within a
                    reasonable time, you are entitled to request a partial or
                    full refund.
                  </li>
                </ol>
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>
                5. How to Request a Refund and Dispute Resolution
              </SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                If you encounter an issue that you cannot resolve directly with
                your Service Provider, our Support Team is here to help. To
                request a refund: We process personal data based on the
                following legal bases:
                <br />
                <ol>
                  <li>Navigate to Help Page dashboard.</li>
                  <li>Click Contact Support. </li>
                  <li>
                    Provide a clear description of the problem and attach any
                    relevant evidence (such as photos of the incomplete or
                    faulty work).
                  </li>
                  <li>
                    Write unique Task ID (you can find it in Task Details).
                  </li>
                  <li>
                    Investigation: Womapp reserves the right to request
                    statements and evidence from both the Customer and the
                    Service Provider before making a final decision on the
                    dispute to ensure a fair resolution.
                  </li>
                </ol>
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>6. Processing Times:</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                As stated in our Help Centre, to request a refund, please
                contact our Support Team directly. We review all requests
                individually and will process your refund within 48 hours if
                eligible. Once a refund is approved and processed on our end,
                please allow 5-7 business days for the funds to appear on your
                original payment method, depending on your bank or credit card
                provider. 
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>7. Non-Refundable Items </SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Please note that refunds will not be provided in the following
                scenarios: 
                <br />
                <ul>
                  <li>
                    You change your mind after the service has been fully and
                    correctly completed.
                  </li>
                  <li>You damage the completed work yourself.</li>
                  <li>
                    The service was completed to a reasonable standard, but you
                    simply do not like the outcome (unless the outcome
                    explicitly violates the agreed-upon task description).
                  </li>
                </ul>
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>
        </PrivacyContainer>

        {/* Get Started CTA */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mt: '48px',
          }}
        >
          <GetStarted />
        </Box>
      </Container>
    </GlassHeroContainer>
  );
};

// Main container - Frame 8
const PrivacyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '0px',
  gap: '24px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    gap: '16px',
  },
}));

// Frame 4 - Header Section
const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '48px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Frame 5 - Heading Wrapper
const HeadingWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Main Heading - Word of Mouth Ltd
const MainHeading = styled(Typography)(({ theme }) => ({
  width: '100%',
  maxWidth: '760px',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '60px',
  lineHeight: '120%',
  textAlign: 'center',
  letterSpacing: '-0.5px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',

  [theme.breakpoints.down('md')]: {
    fontSize: '48px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: '133%',
    letterSpacing: 0,
  },
}));

// Frame 6 - Sub Header Row
const SubHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
}));

// Last Updated
const LastUpdated = styled(Typography)(({ theme }) => ({
  margin: '0 auto',
  fontFamily: 'Roboto, sans-serif',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '175%',
  textAlign: 'center',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',

  [theme.breakpoints.down('sm')]: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '166%',
    letterSpacing: '0.4px',
  },
}));

// Content Section
const ContentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '8px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Section Title Wrapper
const SectionTitleWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Section Title
const SectionTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '175%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',
  textAlign: 'left',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    lineHeight: '157%',
    letterSpacing: '0.1px',
  },
}));

// Section Content Wrapper
const SectionContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Body Text
const BodyText = styled(Box)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',
  textAlign: 'left',

  '& ul, & ol': {
    margin: 0,
    paddingLeft: '20px',
  },
  '& ul': {
    listStyle: 'none',
  },
  '&  ul li:before': {
    content: '"•"',
    position: 'relative',
    left: '-12px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
}));

export default RefundPage;
